/**
 * Settings Page
 * License, API configuration, and Analysis feature settings
 */

import { useState, useEffect, useCallback } from 'react';
import { Key, Bot, User, Building2, Loader2, CheckCircle2, XCircle, AlertTriangle, Eye, EyeOff, Tags, BarChart2, Search, Briefcase, Globe, Edit2, Save, X, Lock } from 'lucide-react';
import { sitesApi, authApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSite } from '../../hooks/useSite';
import {
  structuralTagsApi,
  businessContextApi,
  BUSINESS_MODELS,
  type HubSpotTagWithStructural,
  type BusinessContextProfile,
  type BusinessContextSector,
} from '../../lib/dashboard-api';

type SettingsTab = 'license' | 'api' | 'analysis' | 'business' | 'account' | 'organization';

export default function SettingsPage() {
  const { user, organizations } = useAuth();
  const {
    currentSite,
    licenseStatus,
    claudeStatus,
    activateLicense,
    clearLicense,
    testClaudeKey,
    saveClaudeKey,
  } = useSite();

  const [activeTab, setActiveTab] = useState<SettingsTab>('license');

  // License state
  const [licenseKey, setLicenseKey] = useState('');
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [licenseMessage, setLicenseMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Claude API state
  const [claudeApiKey, setClaudeApiKey] = useState('');
  const [showClaudeKey, setShowClaudeKey] = useState(false);
  const [claudeLoading, setClaudeLoading] = useState(false);
  const [claudeMessage, setClaudeMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Analysis settings state
  const [allTags, setAllTags] = useState<HubSpotTagWithStructural[]>([]);
  const [selectedStructuralIds, setSelectedStructuralIds] = useState<Set<string>>(new Set());
  const [tagSearch, setTagSearch] = useState('');
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagsSaving, setTagsSaving] = useState(false);
  const [tagsMessage, setTagsMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [enableStructureAnalysis, setEnableStructureAnalysis] = useState(true);
  const [enableCategoryRecommendation, setEnableCategoryRecommendation] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Business Context state
  const [bcProfile, setBcProfile] = useState<BusinessContextProfile | null>(null);
  const [bcSectors, setBcSectors] = useState<BusinessContextSector[]>([]);
  const [bcLoading, setBcLoading] = useState(false);
  const [bcEditing, setBcEditing] = useState(false);
  const [bcSelectedIndustry, setBcSelectedIndustry] = useState('');
  const [bcSelectedModel, setBcSelectedModel] = useState('');
  const [bcSecondaryIndustries, setBcSecondaryIndustries] = useState<string[]>([]);
  const [bcSecondaryPicker, setBcSecondaryPicker] = useState('');
  const [bcSaving, setBcSaving] = useState(false);
  const [bcMessage, setBcMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [bcPackNote, setBcPackNote] = useState<string | null>(null);
  const [bcNoLicense, setBcNoLicense] = useState(false);

  // Blog archive URL editing state (HTML clients only)
  const [editingArchiveUrl, setEditingArchiveUrl] = useState(false);
  const [archiveUrlDraft, setArchiveUrlDraft] = useState('');
  const [archiveUrlSaving, setArchiveUrlSaving] = useState(false);
  const [archiveUrlMessage, setArchiveUrlMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMessage, setPwMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSaveArchiveUrl = async () => {
    if (!currentSite || !archiveUrlDraft.trim().startsWith('http')) {
      setArchiveUrlMessage({ type: 'error', text: 'URL must start with https://' });
      return;
    }
    setArchiveUrlSaving(true);
    setArchiveUrlMessage(null);
    try {
      const result = await sitesApi.update(currentSite.id, { blogArchiveUrl: archiveUrlDraft.trim() });
      if (result.success) {
        setArchiveUrlMessage({ type: 'success', text: 'Blog archive URL updated.' });
        setEditingArchiveUrl(false);
        // Refresh user so the new URL propagates through the app
        window.location.reload();
      } else {
        setArchiveUrlMessage({ type: 'error', text: result.error || 'Failed to update URL' });
      }
    } catch {
      setArchiveUrlMessage({ type: 'error', text: 'Failed to update URL' });
    } finally {
      setArchiveUrlSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMessage(null);

    if (newPassword !== confirmNewPassword) {
      setPwMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (newPassword.length < 12) {
      setPwMessage({ type: 'error', text: 'New password must be at least 12 characters' });
      return;
    }

    setPwLoading(true);
    try {
      const result = await authApi.changePassword(currentPassword, newPassword);
      if (result.success) {
        setPwMessage({ type: 'success', text: 'Password changed successfully.' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
      } else {
        setPwMessage({ type: 'error', text: result.message || 'Failed to change password' });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to change password';
      setPwMessage({ type: 'error', text: msg });
    } finally {
      setPwLoading(false);
    }
  };

  const tabs = [
    { id: 'license' as const, name: 'License', icon: Key },
    { id: 'api' as const, name: 'API Keys', icon: Bot },
    { id: 'analysis' as const, name: 'Analysis', icon: BarChart2 },
    { id: 'business' as const, name: 'Business Context', icon: Briefcase },
    { id: 'account' as const, name: 'Account', icon: User },
    { id: 'organization' as const, name: 'Organization', icon: Building2 },
  ];

  // Load analysis settings when the analysis tab becomes active
  const loadAnalysisSettings = useCallback(async () => {
    if (!currentSite) return;
    setTagsLoading(true);
    setSettingsLoading(true);
    try {
      const [tagsResp, settingsResp] = await Promise.all([
        structuralTagsApi.getAll(currentSite.id),
        structuralTagsApi.getSettings(currentSite.id)
      ]);
      setAllTags(tagsResp.tags);
      setSelectedStructuralIds(new Set(tagsResp.structuralTagIds));
      setEnableStructureAnalysis(settingsResp.enableStructureAnalysis);
      setEnableCategoryRecommendation(settingsResp.enableCategoryRecommendation);
      // For HTML clients with no categories yet, show a hint
      if ((tagsResp as { htmlClient?: boolean }).htmlClient && tagsResp.tags.length === 0) {
        setTagsMessage({ type: 'info', text: 'Categories will be available after your first baseline analysis.' });
      }
    } catch {
      setTagsMessage({ type: 'error', text: 'Failed to load analysis settings' });
    } finally {
      setTagsLoading(false);
      setSettingsLoading(false);
    }
  }, [currentSite]);

  const loadBusinessContext = useCallback(async () => {
    if (!currentSite) return;
    setBcLoading(true);
    setBcMessage(null);
    setBcPackNote(null);
    try {
      const [profileResp, industriesResp] = await Promise.all([
        businessContextApi.getProfile(currentSite.id),
        businessContextApi.getIndustries(currentSite.id),
      ]);

      if (profileResp.noLicense) {
        setBcNoLicense(true);
      } else {
        setBcNoLicense(false);
        setBcProfile(profileResp.data);
        if (profileResp.data) {
          setBcSelectedIndustry(profileResp.data.industry_code);
          setBcSelectedModel(profileResp.data.business_model);
          setBcSecondaryIndustries(profileResp.data.secondary_industry_codes ?? []);
        }
      }

      if (industriesResp.data?.sectors) {
        setBcSectors(industriesResp.data.sectors);
      }
    } catch {
      setBcMessage({ type: 'error', text: 'Failed to load business profile. Please try again.' });
    } finally {
      setBcLoading(false);
    }
  }, [currentSite]);

  useEffect(() => {
    if (activeTab === 'analysis') loadAnalysisSettings();
    if (activeTab === 'business') loadBusinessContext();
  }, [activeTab, loadAnalysisSettings, loadBusinessContext]);

  const handleSaveBusinessContext = async () => {
    if (!currentSite || !bcSelectedIndustry || !bcSelectedModel) return;
    setBcSaving(true);
    setBcMessage(null);
    setBcPackNote(null);
    try {
      const resp = await businessContextApi.saveProfile(
        currentSite.id,
        bcSelectedIndustry,
        bcSelectedModel,
        bcSecondaryIndustries,
      );
      if (resp.success && resp.data) {
        // Resolve industry_name locally if BKA sync failed and didn't return it
        const profile = resp.data;
        if (!profile.industry_name && allIndustriesFlat.length > 0) {
          profile.industry_name = allIndustriesFlat.find(i => i.code === profile.industry_code)?.name ?? profile.industry_code;
        }
        setBcProfile(profile);
        setBcSecondaryIndustries(profile.secondary_industry_codes ?? bcSecondaryIndustries);
        setBcEditing(false);
        if ((resp as { sync_warning?: string }).sync_warning) {
          setBcMessage({ type: 'info', text: 'Saved locally. (Server sync pending — try again later.)' });
        } else {
          setBcMessage({ type: 'success', text: 'Business profile saved successfully.' });
        }
        if (!profile.pack_loaded && profile.pack_note) {
          setBcPackNote(profile.pack_note);
        }
      } else {
        setBcMessage({ type: 'error', text: resp.message || 'Failed to save business profile.' });
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setBcMessage({ type: 'error', text: e?.response?.data?.message || 'Failed to save business profile.' });
    } finally {
      setBcSaving(false);
    }
  };

  // Build a flat code → name lookup for all industries across all sectors
  const allIndustriesFlat = bcSectors.flatMap(s => s.industries.map(ind => ({ code: ind.code, name: ind.name, sector: s.name, packStar: ind.pack_status === 'loaded' })));
  const industryNameByCode = Object.fromEntries(allIndustriesFlat.map(i => [i.code, i.name]));

  const handleToggleStructuralTag = (tagId: string) => {
    setSelectedStructuralIds(prev => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
    setTagsMessage(null);
  };

  const handleSaveStructuralTags = async () => {
    if (!currentSite) return;
    setTagsSaving(true);
    setTagsMessage(null);
    try {
      await structuralTagsApi.save(currentSite.id, Array.from(selectedStructuralIds));
      setTagsMessage({ type: 'success', text: isHtmlClient ? 'Structural categories saved successfully' : 'Structural tags saved successfully' });
    } catch {
      setTagsMessage({ type: 'error', text: 'Failed to save structural tags' });
    } finally {
      setTagsSaving(false);
    }
  };

  const handleToggleFeatureSetting = async (setting: 'structure' | 'category', value: boolean) => {
    if (!currentSite) return;
    try {
      if (setting === 'structure') {
        setEnableStructureAnalysis(value);
        await structuralTagsApi.saveSettings(currentSite.id, {
          enableStructureAnalysis: value,
          enableCategoryRecommendation
        });
      } else {
        setEnableCategoryRecommendation(value);
        await structuralTagsApi.saveSettings(currentSite.id, {
          enableStructureAnalysis,
          enableCategoryRecommendation: value
        });
      }
    } catch {
      // Revert on error
      if (setting === 'structure') setEnableStructureAnalysis(!value);
      else setEnableCategoryRecommendation(!value);
    }
  };

  const isHtmlClient = currentSite?.cmsType === 'html' || currentSite?.platform === 'html';

  // Count posts that would be excluded (structural-only posts)
  const structuralOnlyExcluded = allTags.reduce((count, tag) => {
    return count + (selectedStructuralIds.has(tag.id) ? 1 : 0);
  }, 0);

  // Handle license activation
  const handleActivateLicense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!licenseKey.trim() || !currentSite) return;

    setLicenseLoading(true);
    setLicenseMessage(null);

    try {
      const response = await activateLicense(licenseKey.trim());
      
      if (response.success) {
        setLicenseMessage({ type: 'success', text: 'License activated successfully!' });
        setLicenseKey('');
      } else {
        setLicenseMessage({ type: 'error', text: response.error || 'Failed to activate license' });
      }
    } catch (err) {
      const error = err as Error;
      setLicenseMessage({ type: 'error', text: error.message || 'Failed to activate license' });
    } finally {
      setLicenseLoading(false);
    }
  };

  // Handle license clear
  const handleClearLicense = async () => {
    if (!currentSite) return;
    
    setLicenseLoading(true);
    setLicenseMessage(null);

    try {
      await clearLicense();
      setLicenseMessage({ type: 'success', text: 'License deactivated' });
    } catch (err) {
      const error = err as Error;
      setLicenseMessage({ type: 'error', text: error.message || 'Failed to clear license' });
    } finally {
      setLicenseLoading(false);
    }
  };

  // Handle Claude API test
  const handleTestClaudeKey = async () => {
    if (!claudeApiKey.trim() || !currentSite) return;

    setClaudeLoading(true);
    setClaudeMessage(null);

    try {
      const response = await testClaudeKey(claudeApiKey.trim());
      
      if (response.success && response.valid) {
        setClaudeMessage({ type: 'success', text: 'API key is valid!' });
        // Auto-save after successful test — markValid: true so backend stores status as 'valid' immediately
        await saveClaudeKey(claudeApiKey.trim(), true);
        setClaudeApiKey('');
      } else {
        setClaudeMessage({ type: 'error', text: response.message || 'Invalid API key' });
      }
    } catch (err) {
      const error = err as Error;
      setClaudeMessage({ type: 'error', text: error.message || 'Failed to test API key' });
    } finally {
      setClaudeLoading(false);
    }
  };

  // No site selected
  if (!currentSite) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-[var(--text-secondary)] mt-1">
            Manage your account, license, and API configuration
          </p>
        </div>
        <div className="glass rounded-xl p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-[var(--color-warning)] mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Site Selected</h2>
          <p className="text-[var(--text-secondary)]">
            Connect a site to configure settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-[var(--text-secondary)] mt-1">
          Manage your license, API keys, and account settings
        </p>
      </div>

      {/* HTML client: Blog Archive URL card */}
      {currentSite?.cmsType === 'html' && (
        <div className="glass rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#60a5fa]" />
            <h3 className="font-semibold">Blog Configuration</h3>
          </div>

          {/* Archive URL row */}
          <div className="space-y-1">
            <p className="text-xs text-white/50 uppercase tracking-wide">Blog Archive URL</p>
            {editingArchiveUrl ? (
              <div className="flex gap-2 items-center">
                <input
                  type="url"
                  value={archiveUrlDraft}
                  onChange={e => setArchiveUrlDraft(e.target.value)}
                  placeholder="https://example.com/blog"
                  className="flex-1 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#60a5fa] transition-colors"
                />
                <button
                  onClick={handleSaveArchiveUrl}
                  disabled={archiveUrlSaving}
                  className="flex items-center gap-1.5 px-3 py-2 bg-[#ffdc18] text-gray-900 font-medium text-sm rounded-lg hover:bg-[#e6c616] transition-colors disabled:opacity-50"
                >
                  {archiveUrlSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </button>
                <button
                  onClick={() => { setEditingArchiveUrl(false); setArchiveUrlMessage(null); }}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[#60a5fa] break-all">
                  {currentSite.blogArchiveUrl || currentSite.siteUrl || '—'}
                </span>
                <button
                  onClick={() => {
                    setArchiveUrlDraft(currentSite.blogArchiveUrl || currentSite.siteUrl || '');
                    setArchiveUrlMessage(null);
                    setEditingArchiveUrl(true);
                  }}
                  className="flex-shrink-0 flex items-center gap-1.5 text-xs text-white/50 hover:text-white px-2 py-1 rounded hover:bg-white/10 transition-colors"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  Edit
                </button>
              </div>
            )}
            {archiveUrlMessage && (
              <p className={`text-xs mt-1 ${archiveUrlMessage.type === 'success' ? 'text-[var(--color-success)]' : 'text-[var(--color-error)]'}`}>
                {archiveUrlMessage.text}
              </p>
            )}
          </div>

          {/* Site URL (domain) */}
          <div>
            <p className="text-xs text-white/50 uppercase tracking-wide">Domain</p>
            <p className="text-sm text-white/70">{currentSite.siteUrl || '—'}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-[var(--bg-secondary)] rounded-xl overflow-x-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all
                ${activeTab === tab.id
                  ? 'bg-[#ffdc18] text-gray-900'
                  : 'text-[var(--text-secondary)] hover:text-white hover:bg-[var(--bg-tertiary)]'
                }
              `}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="glass rounded-xl p-6 animate-slide-up">
        {/* License Tab */}
        {activeTab === 'license' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">License Configuration</h2>
            
            {/* Current Status */}
            <div className="p-4 bg-[var(--bg-tertiary)] rounded-lg space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-[var(--text-secondary)]">Status</span>
                <span className={`px-2 py-1 rounded text-sm ${
                  licenseStatus?.status === 'active' 
                    ? 'bg-[var(--color-success)]/20 text-[var(--color-success)]'
                    : 'bg-[var(--color-warning)]/20 text-[var(--color-warning)]'
                }`}>
                  {licenseStatus?.status || 'Not Activated'}
                </span>
              </div>
              {licenseStatus?.tier && (
                <div className="flex justify-between items-center">
                  <span className="text-[var(--text-secondary)]">Tier</span>
                  <span className="font-medium uppercase">{licenseStatus.tier}</span>
                </div>
              )}
              {licenseStatus?.expiresAt && (
                <div className="flex justify-between items-center">
                  <span className="text-[var(--text-secondary)]">Expires</span>
                  <span className="text-sm">{new Date(licenseStatus.expiresAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {/* Message */}
            {licenseMessage && (
              <div className={`p-3 rounded-lg flex items-center gap-2 ${
                licenseMessage.type === 'success'
                  ? 'bg-[var(--color-success)]/10 border border-[var(--color-success)]/30 text-[var(--color-success)]'
                  : 'bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-[var(--color-error)]'
              }`}>
                {licenseMessage.type === 'success' ? (
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 flex-shrink-0" />
                )}
                <span className="text-sm">{licenseMessage.text}</span>
              </div>
            )}

            {/* Activation Form */}
            {licenseStatus?.status !== 'active' ? (
              <form onSubmit={handleActivateLicense} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">License Key</label>
                  {licenseStatus?.hasStoredKey && (
                    <p className="text-xs text-[var(--text-secondary)] mb-2">
                      A key is on file ({licenseStatus.storedKeyMasked}) — re-enter it below to activate.
                    </p>
                  )}
                  <input
                    type="text"
                    value={licenseKey}
                    onChange={(e) => setLicenseKey(e.target.value)}
                    placeholder="PLM-XXXXXXXXXXXXXXXX"
                    autoComplete="off"
                    className="w-full px-4 py-3 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[#ffdc18] focus:ring-1 focus:ring-[#ffdc18] transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={licenseLoading || !licenseKey.trim()}
                  className="px-6 py-3 bg-[#ffdc18] hover:bg-[#e6c616] text-gray-900 font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                >
                  {licenseLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Key className="w-4 h-4" />
                  )}
                  Activate License
                </button>
              </form>
            ) : (
              <button
                onClick={handleClearLicense}
                disabled={licenseLoading}
                className="px-4 py-2 bg-[var(--color-error)]/10 text-[var(--color-error)] border border-[var(--color-error)]/30 rounded-lg hover:bg-[var(--color-error)]/20 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {licenseLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Deactivate License
              </button>
            )}

            {/* Get License Link */}
            <p className="text-sm text-white/70">
              Don't have a license?{' '}
              <a
                href="https://vizzex.ai/pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#ffdc18] hover:underline"
              >
                Get one here
              </a>
            </p>
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'api' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-2">Claude API Configuration</h2>
              <p className="text-[var(--text-secondary)] text-sm">
                VizzEx Pro uses Claude AI for advanced content analysis and link text generation.
              </p>
            </div>

            {/* Current Status */}
            <div className="p-4 bg-[var(--bg-tertiary)] rounded-lg space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-[var(--text-secondary)]">Status</span>
                <span className={`px-2 py-1 rounded text-sm ${
                  claudeStatus?.status === 'valid'
                    ? 'bg-[var(--color-success)]/20 text-[var(--color-success)]'
                    : claudeStatus?.status === 'invalid'
                    ? 'bg-[var(--color-error)]/20 text-[var(--color-error)]'
                    : 'bg-[var(--color-warning)]/20 text-[var(--color-warning)]'
                }`}>
                  {claudeStatus?.status === 'valid' ? 'Valid' : 
                   claudeStatus?.status === 'invalid' ? 'Invalid' : 'Not Configured'}
                </span>
              </div>
              {claudeStatus?.hasApiKey && claudeStatus.apiKeyMasked && (
                <div className="flex justify-between items-center">
                  <span className="text-[var(--text-secondary)]">API Key</span>
                  <span className="font-mono text-sm">{claudeStatus.apiKeyMasked}</span>
                </div>
              )}
            </div>

            {/* Message */}
            {claudeMessage && (
              <div className={`p-3 rounded-lg flex items-center gap-2 ${
                claudeMessage.type === 'success'
                  ? 'bg-[var(--color-success)]/10 border border-[var(--color-success)]/30 text-[var(--color-success)]'
                  : 'bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-[var(--color-error)]'
              }`}>
                {claudeMessage.type === 'success' ? (
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 flex-shrink-0" />
                )}
                <span className="text-sm">{claudeMessage.text}</span>
              </div>
            )}

            {/* API Key Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Claude API Key</label>
                <div className="relative">
                  <input
                    type={showClaudeKey ? 'text' : 'password'}
                    value={claudeApiKey}
                    onChange={(e) => setClaudeApiKey(e.target.value)}
                    placeholder="sk-ant-api03-..."
                    className="w-full px-4 py-3 pr-12 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[#ffdc18] focus:ring-1 focus:ring-[#ffdc18] transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowClaudeKey(!showClaudeKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-[var(--bg-secondary)] rounded"
                  >
                    {showClaudeKey ? (
                      <EyeOff className="w-4 h-4 text-white/70" />
                    ) : (
                      <Eye className="w-4 h-4 text-white/70" />
                    )}
                  </button>
                </div>
              </div>
              <button
                onClick={handleTestClaudeKey}
                disabled={claudeLoading || !claudeApiKey.trim()}
                className="px-6 py-3 bg-[#ffdc18] hover:bg-[#e6c616] text-gray-900 font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
              >
                {claudeLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Bot className="w-4 h-4" />
                )}
                Test & Save API Key
              </button>
            </div>

            {/* Get API Key Link */}
            <p className="text-sm text-white/70">
              Get your API key from{' '}
              <a
                href="https://console.anthropic.com/settings/keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#ffdc18] hover:underline"
              >
                Anthropic Console
              </a>
            </p>
          </div>
        )}

        {/* Analysis Tab */}
        {activeTab === 'analysis' && (
          <div className="space-y-8">
            {/* Analysis Features Section — shown first */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-[#ffdc18]" />
                <h2 className="text-lg font-semibold">Analysis Features</h2>
              </div>

              {settingsLoading ? (
                <div className="flex items-center gap-2 text-[var(--text-secondary)] py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Loading settings...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Heading Structure Analysis toggle */}
                  <div className="p-4 bg-[var(--bg-tertiary)] rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-medium text-sm">Heading Structure Analysis</p>
                        <p className="text-white/80 text-sm mt-1.5 leading-relaxed">
                          When ON, each &ldquo;Create Link Text&rdquo; call also analyzes the post&apos;s H2/H3/H4
                          heading structure and returns recommendations. Tracked per post; once complete,
                          subsequent calls skip re-analysis unless requested.
                        </p>
                      </div>
                      <button
                        onClick={() => handleToggleFeatureSetting('structure', !enableStructureAnalysis)}
                        className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors ${
                          enableStructureAnalysis ? 'bg-[#ffdc18]' : 'bg-[var(--bg-secondary)]'
                        }`}
                      >
                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          enableStructureAnalysis ? 'translate-x-7' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  </div>

                  {/* Primary Tag Recommendation toggle */}
                  <div className="p-4 bg-[var(--bg-tertiary)] rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-medium text-sm">Primary Tag Recommendation</p>
                        <p className="text-white/80 text-sm mt-1.5 leading-relaxed">
                          When ON, if a post has more than one tag, VizzEx will recommend which tag is
                          the strongest primary classification for that post. Displayed on the Link
                          Text card.
                        </p>
                      </div>
                      <button
                        onClick={() => handleToggleFeatureSetting('category', !enableCategoryRecommendation)}
                        className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors ${
                          enableCategoryRecommendation ? 'bg-[#ffdc18]' : 'bg-[var(--bg-secondary)]'
                        }`}
                      >
                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          enableCategoryRecommendation ? 'translate-x-7' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-[var(--bg-tertiary)]" />

            {/* Structural Tags Section — shown second */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Tags className="w-5 h-5 text-[#ffdc18]" />
                  <h2 className="text-lg font-semibold">{isHtmlClient ? 'Structural Categories' : 'Structural Tags'}</h2>
                </div>
                <p className="text-white/80 text-sm mt-1.5 leading-relaxed">
                  {isHtmlClient ? (
                    <>
                      Categories used for site navigation or taxonomy — not topical content. Mark any
                      structural categories below so they are excluded from analysis payloads. Posts whose
                      only category is structural will be excluded from analysis entirely.
                    </>
                  ) : (
                    <>
                      Tags used for site navigation or taxonomy — not topical content. Structural tags are
                      excluded from analysis payloads. Published posts that have <strong>only</strong> structural
                      tags are excluded from analysis entirely. Published posts with no tags are shown as
                      &ldquo;Published with No Tag&rdquo; in the Dashboard and uploaded to analysis as &ldquo;uncategorized&rdquo;.
                    </>
                  )}
                </p>
              </div>

              {tagsLoading ? (
                <div className="flex items-center gap-2 text-[var(--text-secondary)] py-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isHtmlClient ? 'Loading categories...' : 'Loading tags...'}</span>
                </div>
              ) : (
                <>
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
                    <input
                      type="text"
                      value={tagSearch}
                      onChange={e => setTagSearch(e.target.value)}
                      placeholder={isHtmlClient ? 'Search categories...' : 'Search tags...'}
                      className="w-full pl-9 pr-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[#ffdc18] focus:ring-1 focus:ring-[#ffdc18] text-sm transition-colors"
                    />
                  </div>

                  {/* Tag list */}
                  <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                    {allTags
                      .filter(tag => tag.name.toLowerCase().includes(tagSearch.toLowerCase()))
                      .map(tag => {
                        const isStructural = selectedStructuralIds.has(tag.id);
                        return (
                          <label
                            key={tag.id}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                              isStructural
                                ? 'bg-[#ffdc18]/10 border border-[#ffdc18]/30'
                                : 'bg-[var(--bg-tertiary)] hover:bg-[var(--bg-tertiary)]/80'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isStructural}
                              onChange={() => handleToggleStructuralTag(tag.id)}
                              className="w-4 h-4 rounded accent-[#ffdc18]"
                            />
                            <span className="flex-1 text-sm">{tag.name}</span>
                            {isStructural && (
                              <span className="text-xs text-[#ffdc18] font-medium">Structural</span>
                            )}
                          </label>
                        );
                      })
                    }
                    {allTags.length === 0 && (
                      <p className="text-[var(--text-muted)] text-sm py-2 text-center">
                        {isHtmlClient ? 'No categories found' : 'No tags found'}
                      </p>
                    )}
                  </div>

                  {/* Info notice */}
                  {structuralOnlyExcluded > 0 && (
                    <div className="flex items-start gap-2 p-3 bg-[var(--color-info)]/10 border border-[var(--color-info)]/30 rounded-lg text-sm text-[var(--color-info)]">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{structuralOnlyExcluded} {isHtmlClient ? 'categor' : 'tag'}{structuralOnlyExcluded !== 1 ? (isHtmlClient ? 'ies' : 's') : (isHtmlClient ? 'y' : '')} marked structural. Posts with only {isHtmlClient ? 'these categories' : 'these tags'} will be excluded from analysis.</span>
                    </div>
                  )}

                  {/* Save message */}
                  {tagsMessage && (
                    <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                      tagsMessage.type === 'success'
                        ? 'bg-[var(--color-success)]/10 border border-[var(--color-success)]/30 text-[var(--color-success)]'
                        : tagsMessage.type === 'info'
                          ? 'bg-blue-500/10 border border-blue-500/30 text-blue-300'
                          : 'bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-[var(--color-error)]'
                    }`}>
                      {tagsMessage.type === 'success'
                        ? <CheckCircle2 className="w-4 h-4" />
                        : tagsMessage.type === 'info'
                          ? <span className="w-4 h-4 text-sm">ℹ</span>
                          : <XCircle className="w-4 h-4" />}
                      {tagsMessage.text}
                    </div>
                  )}

                  <button
                    onClick={handleSaveStructuralTags}
                    disabled={tagsSaving}
                    className="px-6 py-2.5 bg-[#ffdc18] hover:bg-[#e6c616] text-gray-900 font-medium rounded-lg disabled:opacity-50 flex items-center gap-2 transition-colors"
                  >
                    {tagsSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {isHtmlClient ? 'Save Structural Categories' : 'Save Structural Tags'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Business Context Tab */}
        {activeTab === 'business' && (
          <div className="space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Briefcase className="w-5 h-5 text-[#ffdc18]" />
                <h2 className="text-lg font-semibold">Business Context</h2>
              </div>
              <p className="text-white/60 text-sm">
                Set your industry and business model to improve external link quality scoring.
                Industry-specific authoritative domains are applied during analysis runs.
              </p>
            </div>

            {/* Loading */}
            {bcLoading && (
              <div className="flex items-center gap-2 text-white/50 py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading business profile…</span>
              </div>
            )}

            {/* No license gate */}
            {!bcLoading && bcNoLicense && (
              <div className="p-4 bg-amber-400/10 border border-amber-400/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-300">License Required</p>
                    <p className="text-xs text-amber-400/70 mt-0.5">
                      Activate your license on the License tab before configuring your business profile.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Message banner */}
            {bcMessage && (
              <div className={`p-3 rounded-lg flex items-start gap-2 text-sm ${
                bcMessage.type === 'success'
                  ? 'bg-[var(--color-success)]/10 border border-[var(--color-success)]/30 text-[var(--color-success)]'
                  : bcMessage.type === 'info'
                  ? 'bg-blue-500/10 border border-blue-500/30 text-blue-300'
                  : 'bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-[var(--color-error)]'
              }`}>
                {bcMessage.type === 'success'
                  ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  : <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                }
                <span>{bcMessage.text}</span>
              </div>
            )}

            {/* Pack note — informational, not an error */}
            {bcPackNote && (
              <div className="p-3 rounded-lg text-sm bg-blue-500/10 border border-blue-500/20 text-blue-300 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{bcPackNote}</span>
              </div>
            )}

            {/* Main content — only when loaded and license active */}
            {!bcLoading && !bcNoLicense && (
              <>
                {/* Read-only profile summary */}
                {bcProfile && !bcEditing && (
                  <div className="p-4 bg-[var(--bg-tertiary)] rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wider text-white/40">Current Profile</span>
                      <button
                        type="button"
                        onClick={() => { setBcEditing(true); setBcMessage(null); setBcPackNote(null); }}
                        className="text-xs text-[#ffdc18] hover:underline"
                      >
                        Edit
                      </button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-white/60">Primary Industry</span>
                        <span className="font-medium">{bcProfile.industry_name}</span>
                      </div>
                      {bcSecondaryIndustries.length > 0 && (
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-white/60 shrink-0">Additional Industries</span>
                          <div className="flex flex-wrap gap-1 justify-end">
                            {bcSecondaryIndustries.map(code => (
                              <span key={code} className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/70">
                                {industryNameByCode[code] ?? code}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-white/60">Business Model</span>
                        <span className="font-medium">{bcProfile.business_model}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-white/60">Domain Pack</span>
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                          bcProfile.pack_loaded
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-white/10 text-white/40'
                        }`}>
                          {bcProfile.pack_loaded ? 'Industry Pack Active ✓' : 'Using General Domains'}
                        </span>
                      </div>
                      {bcProfile.updated_at && (
                        <div className="flex items-center justify-between">
                          <span className="text-white/60">Last Updated</span>
                          <span className="text-xs text-white/40">
                            {new Date(bcProfile.updated_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* No profile yet — setup prompt */}
                {!bcProfile && !bcEditing && (
                  <div className="p-6 bg-[var(--bg-tertiary)] rounded-lg text-center space-y-3">
                    <Briefcase className="w-8 h-8 text-white/20 mx-auto" />
                    <div>
                      <p className="text-sm font-medium text-white">Business Profile Not Set</p>
                      <p className="text-xs text-white/50 mt-1">
                        Set your industry and business model to improve external link quality scoring.
                        This is optional — analysis runs without it using general authoritative domains.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setBcEditing(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[#ffdc18] text-black rounded-lg hover:bg-[#ffdc18]/90 transition-colors"
                    >
                      <Briefcase className="w-4 h-4" />
                      Set Up Business Profile
                    </button>
                  </div>
                )}

                {/* Edit / setup form */}
                {bcEditing && (
                  <div className="space-y-5">
                    {/* Industry selector — grouped by sector */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Industry <span className="text-[var(--color-error)]">*</span>
                      </label>
                      <select
                        value={bcSelectedIndustry}
                        onChange={e => setBcSelectedIndustry(e.target.value)}
                        className="w-full px-3 py-2.5 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#ffdc18]/50 focus:ring-1 focus:ring-[#ffdc18]/30"
                      >
                        <option value="" disabled>Select your industry…</option>
                        {bcSectors.map(sector => (
                          <optgroup key={sector.code} label={sector.name}>
                            {sector.industries.map(ind => (
                              <option key={ind.code} value={ind.code}>
                                {ind.name}{ind.pack_status === 'loaded' ? ' ★' : ''}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <p className="text-xs text-white/40 mt-1.5">
                        ★ Industries with an active domain pack get enhanced external link scoring.
                        All industries work — unstarred ones use general authoritative domains.
                      </p>
                    </div>

                    {/* Additional Industries */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Additional Industries</label>
                      <p className="text-xs text-white/40 mb-3">
                        If your business spans more than one industry, add secondary industries here.
                        This expands the expertise topics suggested for your authors and improves the
                        relevance of external link recommendations. Your primary industry still
                        determines your organization's schema type.
                      </p>

                      {/* Picker row */}
                      <div className="flex gap-2 mb-2">
                        <select
                          value={bcSecondaryPicker}
                          onChange={e => setBcSecondaryPicker(e.target.value)}
                          disabled={bcSecondaryIndustries.length >= 3}
                          className="flex-1 px-3 py-2.5 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#ffdc18]/50 focus:ring-1 focus:ring-[#ffdc18]/30 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <option value="">— Add an industry —</option>
                          {bcSectors.map(sector => (
                            <optgroup key={sector.code} label={sector.name}>
                              {sector.industries
                                .filter(ind => ind.code !== bcSelectedIndustry && !bcSecondaryIndustries.includes(ind.code))
                                .map(ind => (
                                  <option key={ind.code} value={ind.code}>
                                    {ind.name}{ind.pack_status === 'loaded' ? ' ★' : ''}
                                  </option>
                                ))
                              }
                            </optgroup>
                          ))}
                        </select>
                        <button
                          type="button"
                          disabled={!bcSecondaryPicker || bcSecondaryIndustries.length >= 3}
                          onClick={() => {
                            if (bcSecondaryPicker && bcSecondaryIndustries.length < 3) {
                              setBcSecondaryIndustries(prev => [...prev, bcSecondaryPicker]);
                              setBcSecondaryPicker('');
                            }
                          }}
                          className="px-4 py-2.5 text-sm font-medium bg-white/10 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          + Add
                        </button>
                      </div>
                      {bcSecondaryIndustries.length >= 3 && (
                        <p className="text-xs text-amber-400/80 mt-1">Maximum of 3 additional industries reached. Remove one to add another.</p>
                      )}

                      {/* Chip list of selected secondaries */}
                      {bcSecondaryIndustries.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {bcSecondaryIndustries.map(code => (
                            <span
                              key={code}
                              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs rounded-full bg-[#ffdc18]/10 border border-[#ffdc18]/30 text-[#ffdc18]"
                            >
                              {industryNameByCode[code] ?? code}
                              <button
                                type="button"
                                onClick={() => setBcSecondaryIndustries(prev => prev.filter(c => c !== code))}
                                className="w-3.5 h-3.5 rounded-full flex items-center justify-center hover:bg-[#ffdc18]/30 transition-colors text-[10px] leading-none"
                                aria-label={`Remove ${industryNameByCode[code] ?? code}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Business model — radio cards */}
                    <div>
                      <label className="block text-sm font-medium mb-3">
                        Business Model <span className="text-[var(--color-error)]">*</span>
                      </label>
                      <div className="space-y-2">
                        {BUSINESS_MODELS.map(model => (
                          <label
                            key={model.value}
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              bcSelectedModel === model.value
                                ? 'border-[#ffdc18]/50 bg-[#ffdc18]/5'
                                : 'border-white/10 bg-[var(--bg-tertiary)] hover:border-white/20'
                            }`}
                          >
                            <input
                              type="radio"
                              name="businessModel"
                              value={model.value}
                              checked={bcSelectedModel === model.value}
                              onChange={() => setBcSelectedModel(model.value)}
                              className="mt-0.5 accent-[#ffdc18]"
                            />
                            <div>
                              <span className="text-sm font-medium">{model.value}</span>
                              <span className="text-sm text-white/50 ml-1.5">— {model.description}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-1">
                      <button
                        type="button"
                        onClick={handleSaveBusinessContext}
                        disabled={bcSaving || !bcSelectedIndustry || !bcSelectedModel}
                        className="px-5 py-2.5 bg-[#ffdc18] text-black text-sm font-medium rounded-lg hover:bg-[#ffdc18]/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {bcSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                        Save Business Profile
                      </button>
                      {bcProfile && (
                        <button
                          type="button"
                          onClick={() => {
                            setBcEditing(false);
                            setBcSelectedIndustry(bcProfile.industry_code);
                            setBcSelectedModel(bcProfile.business_model);
                            setBcSecondaryIndustries(bcProfile.secondary_industry_codes ?? []);
                            setBcSecondaryPicker('');
                            setBcMessage(null);
                            setBcPackNote(null);
                          }}
                          className="px-4 py-2.5 text-sm text-white/60 hover:text-white border border-white/10 hover:border-white/20 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Footer note */}
                <div className="pt-2 border-t border-white/5">
                  <p className="text-xs text-white/30 leading-relaxed">
                    Your business profile takes effect on the next analysis run. It is optional —
                    analyses run without it using general authoritative domains. This setting does
                    not affect internal link recommendations.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="space-y-8">
            {/* Profile info (read-only for now) */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Account</h2>
              <div className="p-4 bg-[var(--bg-tertiary)] rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[var(--text-secondary)] text-sm">Email</span>
                  <span className="text-sm font-medium">{user?.email || '—'}</span>
                </div>
                {user?.name && (
                  <div className="flex justify-between items-center">
                    <span className="text-[var(--text-secondary)] text-sm">Name</span>
                    <span className="text-sm font-medium">{user.name}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-[var(--bg-tertiary)]" />

            {/* Change Password */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#ffdc18]" />
                <h3 className="text-base font-semibold">Change Password</h3>
              </div>

              {pwMessage && (
                <div className={`p-3 rounded-lg flex items-center gap-2 ${
                  pwMessage.type === 'success'
                    ? 'bg-[var(--color-success)]/10 border border-[var(--color-success)]/30 text-[var(--color-success)]'
                    : 'bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-[var(--color-error)]'
                }`}>
                  {pwMessage.type === 'success'
                    ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    : <XCircle className="w-4 h-4 flex-shrink-0" />}
                  <span className="text-sm">{pwMessage.text}</span>
                </div>
              )}

              <form onSubmit={handleChangePassword} className="space-y-4">
                {/* Current password */}
                <div>
                  <label className="block text-sm font-medium mb-2">Current Password</label>
                  <div className="relative">
                    <input
                      type={showCurrentPw ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={e => setCurrentPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="w-full px-4 py-3 pr-12 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[#ffdc18] focus:ring-1 focus:ring-[#ffdc18] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPw(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-[var(--bg-secondary)] rounded"
                    >
                      {showCurrentPw ? <EyeOff className="w-4 h-4 text-white/70" /> : <Eye className="w-4 h-4 text-white/70" />}
                    </button>
                  </div>
                </div>

                {/* New password */}
                <div>
                  <label className="block text-sm font-medium mb-2">New Password</label>
                  <div className="relative">
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      required
                      minLength={12}
                      autoComplete="new-password"
                      className="w-full px-4 py-3 pr-12 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[#ffdc18] focus:ring-1 focus:ring-[#ffdc18] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPw(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-[var(--bg-secondary)] rounded"
                    >
                      {showNewPw ? <EyeOff className="w-4 h-4 text-white/70" /> : <Eye className="w-4 h-4 text-white/70" />}
                    </button>
                  </div>
                  <p className="text-xs text-white/50 mt-1">At least 12 characters</p>
                </div>

                {/* Confirm new password */}
                <div>
                  <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={e => setConfirmNewPassword(e.target.value)}
                    required
                    minLength={12}
                    autoComplete="new-password"
                    className="w-full px-4 py-3 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[#ffdc18] focus:ring-1 focus:ring-[#ffdc18] transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={pwLoading || !currentPassword || !newPassword || !confirmNewPassword}
                  className="px-6 py-3 bg-[#ffdc18] hover:bg-[#e6c616] text-gray-900 font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                  Change Password
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Organization Tab */}
        {activeTab === 'organization' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Organization Settings</h2>
            {organizations.length > 0 ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Organization Name</label>
                  <input
                    type="text"
                    defaultValue={organizations[0]?.name || ''}
                    className="w-full px-4 py-3 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[#ffdc18] transition-colors"
                  />
                </div>
                <div className="p-4 bg-[var(--bg-tertiary)] rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-[var(--text-secondary)]">Your Role</span>
                    <span className="px-2 py-1 bg-[#ffdc18]/20 text-[#ffdc18] rounded text-sm capitalize">
                      {organizations[0]?.role}
                    </span>
                  </div>
                </div>
                <button className="px-6 py-3 bg-[#ffdc18] hover:bg-[#e6c616] text-gray-900 font-medium rounded-lg hover:opacity-90 transition-opacity">
                  Save Changes
                </button>
              </div>
            ) : (
              <p className="text-[var(--text-secondary)]">No organization found.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
